import { type GroupLifeCalculateAnalysisPayload } from '@/services/group-life';
import {
  groupLifeProposalsService,
  type GroupLifeCalculateAnalysisResult,
} from '@/services/proposals/group-life';
import { useMutation, useQueryClient } from '@tanstack/react-query';

type Variables = {
  policyId: string;
  proposalId: string;
  payload: GroupLifeCalculateAnalysisPayload;
};

export function useSaveGroupLifeProposalCommercialization() {
  const queryClient = useQueryClient();

  return useMutation<GroupLifeCalculateAnalysisResult, Error, Variables>({
    mutationKey: ['group-life-proposal', 'save-commercialization'],
    retry: false,
    mutationFn: async ({ policyId, payload }) => {
      const calculated =
        await groupLifeProposalsService.calculateProposalAnalysis(
          policyId,
          payload
        );
      if (!calculated.success) {
        throw new Error(calculated.error || 'Erro ao recalcular a proposta');
      }
      return calculated.data ?? {};
    },
    // Nada é aguardado aqui: o backend só responde depois de conferir que o
    // InsureMO persistiu os percentuais, então o front não precisa reler para
    // confirmar. As telas montadas refazem suas próprias queries.
    onSuccess: (result, variables) => {
      const { policyId, proposalId } = variables;
      const detailsKey = ['group-life-proposal', proposalId];

      // Nada mudou → nenhum cache ficou obsoleto.
      if (result.outcome === 'unchanged') return;

      if (result.details) {
        queryClient.setQueryData(detailsKey, result.details);
      } else {
        queryClient.invalidateQueries({ queryKey: detailsKey });
      }
      queryClient.invalidateQueries({
        queryKey: ['group-life', 'quote-details', policyId],
      });

      // Toda mudança de comercialização re-tarifa vida a vida, então os
      // caches por vida (tarifação, vidas da análise) ficam obsoletos.
      queryClient.removeQueries({
        queryKey: ['pme-tarifacao', 'proposal', policyId],
      });
      queryClient.setQueryData(
        ['pme-tarifacao-revision', 'proposal', policyId],
        Date.now()
      );
      queryClient.invalidateQueries({
        queryKey: ['group-life', 'underwriting-messages', policyId],
      });
      queryClient.invalidateQueries({
        queryKey: ['group-life', 'underwriting-messages', proposalId],
      });
      queryClient.invalidateQueries({
        queryKey: ['group-life', 'proposal-lives', policyId],
      });
      queryClient.invalidateQueries({
        queryKey: ['pme-proposal-analysis-members', policyId],
      });
      queryClient.invalidateQueries({
        queryKey: ['pme-proposal-analysis-members', proposalId],
      });
      queryClient.invalidateQueries({ queryKey: ['proposal-analyses'] });
      queryClient.invalidateQueries({ queryKey: ['proposals'] });
    },
  });
}
